import type { WikiCitation } from '@/services/types/matter-wiki-types';

const UUID_RE =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

const INLINE_CITATION_RE =
  /\[source:(?<source_type>[a-z_]+):(?<source_id>[0-9a-fA-F-]{36})(?:#(?<locator_type>[a-z_]+):(?<locator_start>[A-Za-z0-9._:/-]+)(?:\.\.(?<locator_end>[A-Za-z0-9._:/-]+))?)?\]/g;

export type WikiCitationSourceType = 'artifact' | 'task' | 'staff_note' | 'unknown';

export interface WikiCitationFootnote {
  number: number;
  citation: WikiCitation;
}

interface CitationTagParts {
  source_type: string;
  source_id: string;
  locator_type?: string;
  locator_start?: string;
  locator_end?: string;
}

export function normalizeCitationSourceType(sourceType: string): WikiCitationSourceType {
  const normalized = sourceType.toLowerCase();

  if (normalized === 'artifact') {
    return 'artifact';
  }

  if (normalized === 'task') {
    return 'task';
  }

  if (normalized === 'staff_note') {
    return 'staff_note';
  }

  return 'unknown';
}

export function extractCitationTargetId(sourceId: string): string {
  const matched = sourceId.match(UUID_RE);
  return matched?.[0] ?? sourceId;
}

export function getCitationDisplayLabel(citation: WikiCitation): string {
  const sourceType = normalizeCitationSourceType(citation.source_type);
  const id = extractCitationTargetId(String(citation.source_id));
  const shortId = id.length > 8 ? id.slice(0, 8) : id;

  let locatorLabel = '';
  if (citation.locator_type && citation.locator_start) {
    locatorLabel = `#${citation.locator_type}:${citation.locator_start}`;
  }

  if (sourceType === 'artifact') {
    return `Artifact ${shortId}${locatorLabel}`;
  }

  if (sourceType === 'task') {
    return `Task ${shortId}`;
  }

  if (sourceType === 'staff_note') {
    return `Staff Note ${shortId}`;
  }

  return `${citation.source_type}: ${id}${locatorLabel}`;
}

export function getCitationKey(citation: WikiCitation): string {
  return [
    citation.source_type,
    citation.source_id,
    citation.locator_type,
    citation.locator_start,
    citation.locator_end,
  ]
    .filter(Boolean)
    .join('-');
}

function citationMatchesParts(citation: WikiCitation, parts: CitationTagParts): boolean {
  return (
    citation.source_type === parts.source_type &&
    String(citation.source_id) === parts.source_id &&
    (citation.locator_type ?? undefined) === parts.locator_type &&
    (citation.locator_start ?? undefined) === parts.locator_start &&
    (citation.locator_end ?? undefined) === parts.locator_end
  );
}

function findCitationFromTag(parts: CitationTagParts, citations: WikiCitation[]): WikiCitation {
  const exactMatch = citations.find((citation) => citationMatchesParts(citation, parts));
  if (exactMatch) {
    return exactMatch;
  }

  return {
    source_type: parts.source_type,
    source_id: parts.source_id,
    locator_type: parts.locator_type,
    locator_start: parts.locator_start,
    locator_end: parts.locator_end,
  };
}

export function transformWikiContentWithFootnotes(
  content: string,
  citations: WikiCitation[],
): { content: string; footnotes: WikiCitationFootnote[] } {
  const orderedFootnotes: WikiCitationFootnote[] = [];
  const indexByCitationKey = new Map<string, number>();

  const transformedContent = content.replace(
    INLINE_CITATION_RE,
    (
      _match,
      _sourceType,
      _sourceId,
      _locatorType,
      _locatorStart,
      _locatorEnd,
      _offset,
      _input,
      groups,
    ) => {
      const parts: CitationTagParts = {
        source_type: groups?.source_type ?? '',
        source_id: groups?.source_id ?? '',
        locator_type: groups?.locator_type,
        locator_start: groups?.locator_start,
        locator_end: groups?.locator_end,
      };

      const citation = findCitationFromTag(parts, citations);
      const key = getCitationKey(citation);

      let number = indexByCitationKey.get(key);
      if (!number) {
        number = orderedFootnotes.length + 1;
        indexByCitationKey.set(key, number);
        orderedFootnotes.push({ number, citation });
      }

      return `[[${number}]](#wiki-cite-${number})`;
    },
  );

  return {
    content: transformedContent,
    footnotes: orderedFootnotes,
  };
}
